// A folder's checks, the same on both sides: the API refuses with these texts (functions/src/folders/rules.ts),
// and the web app checks first (web/src/folders), so a wrong file or too long notes fail at once.
import { FILE_NAME_MAX, FOLDER_NOTES_MAX } from './vocab.ts';

const MB = 1024 * 1024;

/** A file name as stored: trimmed, NFC (a Mac sends "ą" as two characters). */
export const fileNameOf = (raw: string): string => raw.trim().normalize('NFC');

/** Why a file name (as stored, fileNameOf) is refused; '' when it is fine. */
export function fileNameProblem(name: string): string {
  if (!name) return 'The file has no name.';
  if (name.length > FILE_NAME_MAX) return `That file name is too long (${name.length} characters; at most ${FILE_NAME_MAX}).`;
  // A lone surrogate too (a name cut inside an emoji): the name goes into a Content-Disposition header.
  if (/[\u0000-\u001f\u007f-\u009f/\\]|\p{Cs}/u.test(name)) return 'A file name cannot hold slashes or control characters.';
  return '';
}

/** Why a file of `size` bytes is refused (over `maxMb`, FILES_MAX_MB); '' when it fits. */
export function fileSizeProblem(size: number, maxMb: number): string {
  return size > maxMb * MB ? `That file is too large (at most ${maxMb.toLocaleString('en-US')} MB).` : '';
}

/** "The notes are too long (51,200 characters; at most 50,000)." '' when they fit. */
export function notesLengthProblem(html: string): string {
  if (html.length <= FOLDER_NOTES_MAX) return '';
  return `The notes are too long (${html.length.toLocaleString('en-US')} characters; at most ${FOLDER_NOTES_MAX.toLocaleString('en-US')}).`;
}

/** Types a browser may show in a tab; the rest download. No HTML, no SVG: those run scripts. */
export function isInlineSafe(type: string): boolean {
  if (type === 'image/svg+xml') return false;
  return /^(image|video|audio)\//.test(type) || type === 'application/pdf' || type === 'text/plain';
}

// ── A topic's full description: one file of its folder, shown to anyone on /apply/T-107 while it is open ──
// (functions/src/folders/describe.ts)

/** The web page of an open topic and its full description: /apply/T-107. */
export const topicPagePath = (nodeId: string): string => `/apply/${encodeURIComponent(nodeId)}`;

/**
 * How a file would show as a topic's full description: a PDF by its type (as the preview tells one), a Word
 * file by its name (.docx). null: neither, so it cannot be one.
 */
export function descriptionKind(f: { name: string; type: string }): 'pdf' | 'docx' | null {
  if (f.type === 'application/pdf') return 'pdf';
  return /\.docx$/i.test(f.name) ? 'docx' : null;
}

/** Why a file cannot be a topic's full description, by its kind. */
export const DESCRIPTION_KIND_PROBLEM = 'Only a Word (.docx) or PDF file can be shown as the full description.';

// ── A folder's shared link: /files/T-012/<key>, anyone with it, no sign-in (functions/src/folders/link.ts) ──

/** The web page of a shared folder link. */
export const FOLDER_LINK_PAGE = '/files';

/** Characters of a link's key (base64url): 132 bits. */
export const FOLDER_LINK_KEY_LENGTH = 22;

/** A NodeID a shared link may name: a task or grant, never the Strategy library. */
export const FOLDER_LINK_ID = /^T-\d{1,7}$/;

/** A link's key as it may look: FOLDER_LINK_KEY_LENGTH base64url characters. */
export const FOLDER_LINK_KEY = new RegExp(`^[A-Za-z0-9_-]{${FOLDER_LINK_KEY_LENGTH}}$`);

/** "/files/T-012/<key>" */
export const folderLinkPath = (nodeId: string, key: string): string => `${FOLDER_LINK_PAGE}/${encodeURIComponent(nodeId)}/${encodeURIComponent(key)}`;

/** A shared folder link on any site (an old address too): http(s), its path exactly /files/T-012/<key>. */
export function isFolderLinkUri(uri: string): boolean {
  let u: URL;
  try {
    u = new URL(uri);
  } catch {
    return false;
  }
  const [id = '', key = '', ...more] = u.pathname.startsWith(`${FOLDER_LINK_PAGE}/`) ? u.pathname.slice(FOLDER_LINK_PAGE.length + 1).split('/') : [];
  return /^https?:$/.test(u.protocol) && !more.length && FOLDER_LINK_ID.test(id) && FOLDER_LINK_KEY.test(key);
}
